import {
  saveTemplateFile,
  saveProcessedTemplateFile,
  fetchContentBlocksDetails,
  fetchFirmTemplateFieldDetails,
} from "./CreateOrEditEditorComponent.service";

import { fetchTemplateFieldRules } from "../RulesPage/RulesPage.service";

import {
  CreateOrEditEditorComponentProps,
  EditType
} from "./CreateOrEditEditorComponent";

import { format, parse } from "date-fns";

type RecipientOption = {
  label: string;
  value: string;
};

type RowData = {
  RuleName: string;
  DefaultValue?: string;
  [key: string]: any;
};

export type FieldRulesRowData = RowData[];
type DeletedRowData = RowData[];

export interface HandleRulesModalCloseParams {
  type: string;
  fieldRulesRowData: FieldRulesRowData;
  deletedRowData: DeletedRowData;
  ruleName: string;
  defaultValue: string;
  setAddRulesModalShow: React.Dispatch<React.SetStateAction<boolean>>;
  setRefreshRules: React.Dispatch<React.SetStateAction<boolean>>;
  setTemplateFieldRulesRowData: React.Dispatch<React.SetStateAction<FieldRulesRowData>>;
}

type SaveAndNextParams = {
  originalFileName: string;
  editType: EditType;
  docFileName: string;
  recipients?: string;
  props: CreateOrEditEditorComponentProps;
  templateFieldRulesRowData: any[]; // Replace `any` with proper type if known
  clientDetails: any;               // Replace `any` with proper type if known
  containerRef: React.RefObject<any>; // More specific type if you know the DocumentEditor structure
  setNextStepStatus: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: (props: CreateOrEditEditorComponentProps) => void;
};

type PlaceholderType = "SignatureFields" | "Rules" | "ContentBlocks" | "SelectedFields" | string;

interface EditorContainer {
  documentEditor?: {
    searchModule: {
      findAll: (text: string) => void;
      searchResults: {
        length: number;
        replaceAll: (replacement: string) => void;
        getTextSearchResultsOffset: () => Array<{
          startOffset: string;
          endOffset: string;
        }>;
      };
    };
    selection: {
      select: (start: string, end: string, options?: { preventScroll?: boolean }) => void;
      clear: () => void;
      characterFormat: {
        highlightColor: string;
      };
    };
  };
}
export const getBase64Data = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      try {
        const result = reader.result as string;
        const base64Data = result.split(',')[1]; // Remove the data URL prefix
        resolve(base64Data);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (e) => reject(e);

    reader.readAsDataURL(blob);
  });
};

export const handleSaveAndNextBtnClick = async ({
  originalFileName,
  editType,
  docFileName,
  recipients,
  props,
  templateFieldRulesRowData,
  clientDetails,
  containerRef,
  setNextStepStatus,
  setIsEdit,
  handleClose
}: SaveAndNextParams): Promise<void> => {
  try {
    setNextStepStatus(true);

    const blob = await containerRef.current?.documentEditor.saveAsBlob('Docx');
    const base64Data = await getBase64Data(blob);

    if (editType === "Template") {
      await saveTemplateFile(
        props.templateId!,
        originalFileName,
        base64Data,
        templateFieldRulesRowData,
        recipients!!
      );
    } else {
      await saveProcessedTemplateFile(
        props.processedTemplateId!,
        docFileName,
        props.pdffilename!,
        base64Data,
        clientDetails
      );
    }

    setIsEdit(true);

    setTimeout(() => handleClose(props), 2000);
  } catch (error) {
    console.error("Error saving template:", error);
  } finally {
    setNextStepStatus(false);
  }
};

export const getLabelIndex = (label: string, recipientOptions: RecipientOption[]): number => {
  return recipientOptions.findIndex(option => option.label === label);
};

export const onHandleRulesModalClose = ({
  type,
  fieldRulesRowData,
  deletedRowData,
  ruleName,
  defaultValue,
  setAddRulesModalShow,
  setRefreshRules,
  setTemplateFieldRulesRowData,
}: HandleRulesModalCloseParams): void => {
  setAddRulesModalShow(false);

  if (type && fieldRulesRowData && type.toLowerCase() === "okay") {
    setRefreshRules(true);

    // Remove existing rule data for the given RuleName
    setTemplateFieldRulesRowData((prevRowData) =>
      prevRowData.filter((row) => row.RuleName !== ruleName)
    );

    // Add new row data (fieldRulesRowData and deletedRowData)
    setTemplateFieldRulesRowData((prevRowData) => [
      ...prevRowData,
      ...fieldRulesRowData,
      ...deletedRowData,
    ]);

    // Update DefaultValue for the specified RuleName
    setTemplateFieldRulesRowData((prevRowData) =>
      prevRowData.map((row) =>
        row.RuleName === ruleName ? { ...row, DefaultValue: defaultValue } : row
      )
    );

    setTimeout(() => {
      setRefreshRules(false);
    }, 1000);
  }
};

export const handleClose = (props: CreateOrEditEditorComponentProps) => {
  if (props.onHide) {
    props.onHide();
  }
};
export const handleDrop = async (e: React.DragEvent<HTMLDivElement>, containerRef: React.RefObject<any>) => {
  e.preventDefault();
  const data = e.dataTransfer.getData("text/plain");
  console.trace(data)

  if (data) {
    if (containerRef.current && containerRef.current.documentEditor) {
      try {
        const rect = containerRef.current.documentEditor.element.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top + 20;

        containerRef.current.documentEditor.editor.selection.select({ x: offsetX, y: offsetY, extend: false });
        containerRef.current.documentEditor.editor.insertText(data);
      } catch (error) {
        console.error("Error handling drop:", error);
      }
    } else {
      console.warn("DocumentEditor or container is not available.");
    }
  } else {
    console.warn("No data available to insert.");
  }
};

export const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
  e.preventDefault();
};

export const onCreate = (containerRef: React.RefObject<any>, forwizard: boolean) => {
  if (!containerRef.current) return;

  const editor = containerRef.current?.documentEditor;

  // const layout =  forwizard ? "WebLayout" : "Pages";
  const layout = "Pages";

  if (editor) {
    editor.layoutType = layout;

    if (forwizard) editor.fitPage("FitPageWidth");
  }
  let menuItems: any[] = [];
  containerRef.current.documentEditor.contextMenu.addCustomMenu(menuItems, false);
};

export const handleContentBlockShow = (
  setContentBlockId: React.Dispatch<React.SetStateAction<string | undefined>>,
  setBlockName: React.Dispatch<React.SetStateAction<string>>,
  setBlockContent: React.Dispatch<React.SetStateAction<string>>,
  setActive: React.Dispatch<React.SetStateAction<"Y" | "N">>,
  setStatus: React.Dispatch<React.SetStateAction<string | undefined>>,
  setContentBlockModelShow: React.Dispatch<React.SetStateAction<boolean>>,
  data: any
) => {
  setContentBlockId(data.ContentBlockId);
  setBlockName(data.BlockName);
  setBlockContent(data.BlockContent);
  setActive(data.Active);
  setStatus(data.Status);
  setContentBlockModelShow(true);
};

export const handleContentBlockHide = (
  setContentBlockModelShow: React.Dispatch<React.SetStateAction<boolean>>
) => {
  setContentBlockModelShow(false);
};

/**
 * Converts a Blob to Base64 string
 * @param blob - The blob to convert
 * @returns Promise with the base64 string
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Evaluates a condition based on values and operator
 * @param value - Current field value
 * @param operator - Operator to compare (e.g., '=', '!=', '>', '<', etc.)
 * @param conditionValue - Expected condition value
 * @returns Boolean indicating if condition is met
 */
export const evaluateCondition = (value: any, operator: string, conditionValue: any): boolean => {
  switch (operator) {
    case "=":
      return value.toString() === conditionValue.toString();
    case "!=":
      return value.toString() !== conditionValue.toString();
    case ">":
      return parseFloat(value) > parseFloat(conditionValue);
    case "<":
      return parseFloat(value) < parseFloat(conditionValue);
    case ">=":
      return parseFloat(value) >= parseFloat(conditionValue);
    case "<=":
      return parseFloat(value) <= parseFloat(conditionValue);
    case "contains":
      return value.toString().includes(conditionValue.toString());
    case "not contains":
      return !value.toString().includes(conditionValue.toString());
    default:
      console.warn("Unknown operator:", operator);
      return false;
  }
};

// tiny safe parser for date-fns
const safeParse = (s: string, fmt: string): Date | null => {
  try {
    const d = parse(s, fmt, new Date());
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

type HasDateBits = {
  showTimeSelect?: boolean;
  dateConfig?: {
    displayFormatDate?: string;
    displayFormatDateTime?: string;
    storageFormatDate?: string;
    storageFormatDateTime?: string;
  };
};

export const getDisplayFmt = (data?: HasDateBits): string =>
  data?.showTimeSelect
    ? data?.dateConfig?.displayFormatDateTime ?? "MMMM dd, yyyy h:mm aa"
    : data?.dateConfig?.displayFormatDate ?? "MMMM dd, yyyy";

const getStorageFmt = (data?: HasDateBits): string =>
  data?.showTimeSelect
    ? data?.dateConfig?.storageFormatDateTime ?? "yyyy-MM-dd'T'HH:mm:ssXXX"
    : data?.dateConfig?.storageFormatDate ?? "yyyy-MM-dd";

/**
 * Format a stored date string using the field's own dateConfig
 * Falls back gracefully if parsing fails.
 */
export const formatByConfig = (stored: unknown, data?: HasDateBits): string | null => {
  if (stored == null) return null;

  const raw = String(stored);
  const storageFmt = getStorageFmt(data);
  const displayFmt = getDisplayFmt(data);

  // 1) try parsing with configured storage format
  let dt = safeParse(raw, storageFmt);

  // 2) fallback: try native Date (covers ISO with Z/offset, etc.)
  if (!dt) {
    const d2 = new Date(raw);
    if (!isNaN(d2.getTime())) dt = d2;
  }

  return dt ? format(dt, displayFmt) : raw;
};


/**
 * Processes field replacements in the document
 * @param params - Object containing necessary parameters
 * @returns Promise that resolves when replacement is complete
 */
export const processFieldReplacements = async ({
  containerRef,
  allFieldsMap,
  originalSFDT,
  setRestrictEditing,
  setLoadingEditor,
  lastFocusedFieldRef
}: {
  containerRef: any;
  allFieldsMap: any;
  originalSFDT: any;
  setRestrictEditing: (restrictEditing: boolean) => void;
  setLoadingEditor: (loader: boolean) => void;
  lastFocusedFieldRef?: { current: string | null };
}) => {
  if (!allFieldsMap || !containerRef.current?.documentEditor) return false;

  const startTime = performance.now();
  const replaceTasks = [];
  let isReplaced = false;
  console.log("All Fields Map: ", allFieldsMap)

  for (const key of Object.keys(allFieldsMap)) {
    const items = allFieldsMap[key];
    if (!Array.isArray(items)) continue;

    for (const item of items) {
      const textToFind = item.name;
      let textToAdd = "";

      if (key === "Rules" && Array.isArray(item.data)) {
        for (const rule of item.data) {
          const { RuleName, Operator, ConditionValue, SubstitutionValue, SelectedFieldValue } = rule;

          const selectedField = (allFieldsMap["SelectedFields"] ?? []).find(
            (sf: any) => sf.name === SelectedFieldValue
          );

          if ([undefined, null, ""].includes(selectedField?.value)) continue;

          const conditionMet = evaluateCondition(
            selectedField.value,
            Operator,
            ConditionValue
          );

          if (conditionMet) {
            // If the selected field is a date, use its own config to format
            if (String(selectedField?.type).toLowerCase() === "date") {
              const pretty = formatByConfig(selectedField.value, selectedField.data);
              console.log('PRETTY: ', pretty)
              if (pretty) {
                textToAdd = pretty;
                break;
              }
            }

            // fallback to rule's substitution value
            textToAdd = SubstitutionValue ?? "";
            break;
          }
        }
      }
      else if (key === "ContentBlocks") {
        continue;
      }
      else {
        if (!item.value) continue;
        // If THIS item is a date field, format via its own config
        if (String(item?.data?.type).toLowerCase() === "date") {
          const pretty = formatByConfig(item.value, item.data);
          textToAdd = (pretty ?? String(item.value)).trim();
        } else {
          textToAdd = String(item.value).trim();
        }
      }

      if (textToAdd) {
        replaceTasks.push(async () => {
          console.log("FIND AND REPLACE CALLED: ", key, textToFind, textToAdd);
          await find_and_replace_all(
            containerRef.current,
            key,
            textToFind,
            textToAdd
          );
          isReplaced = true;
        });
      }
    }
  }


  if (replaceTasks.length === 0) {
    console.log("Time: No replacements to make — skipping SFDT open.");
    return false;
  }

  if (!originalSFDT)
    return false;

  setLoadingEditor(true);

  containerRef.current.documentEditor.open(originalSFDT);
  setRestrictEditing(false);

  await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for SFDT to load

  await Promise.all(replaceTasks.map(task => task()));

  setRestrictEditing(true);

  // Restore focus to input field
  setTimeout(() => {
    const activeFieldId = lastFocusedFieldRef?.current;
    if (activeFieldId) {
      const input = document.getElementById(activeFieldId);
      if (input) input.focus();
    }
  }, 10);

  const endTime = performance.now();
  console.log(`Time: Replacement process completed in ${(endTime - startTime).toFixed(2)} ms`);

  return isReplaced;
};

/**
 * Converts document to Base64 after removing unnecessary placeholders
 * @param params - Object containing container reference and field mapping
 * @returns Promise with base64 data
 */
export const convertDocToBase64 = async ({
  containerRef,
  allFieldsMap,
  SaveFormat
}: {
  containerRef: any;
  allFieldsMap: any;
  SaveFormat: any;
}): Promise<string | undefined> => {
  if (!containerRef.current || !containerRef.current.documentEditor) return;

  try {
    const replacePromises = Object.keys(allFieldsMap).map(async (key) => {
      if (key !== "SignatureFields" && Array.isArray(allFieldsMap[key])) {
        for (const item of allFieldsMap[key]) {
          const textToFind = item.name;
          console.log("REMOVE UNNECESSARY FIELDS CALLED: ", key, item.name, item);
          if (containerRef.current && textToFind) {
            console.log("REMOVE UNNECESSARY FIELDS CALLED: ", key, textToFind);
            await remove_unnecessary_placeholders(containerRef.current, key, textToFind);
          }
        }
      }
    });

    await Promise.all(replacePromises);

    const docBlob = await containerRef.current.documentEditor.saveAsBlob(SaveFormat.Docx);
    console.log("DOC BLOB: ", docBlob)
    const base64Data = await blobToBase64(docBlob);
    console.log("Base64 Encoded Document:", base64Data);

    return base64Data;
  } catch (error) {
    console.error("Error converting document to Base64:", error);
  }
};

/**
 * Initializes document editor event listeners
 * @param params - Object containing editor parameters
 * @returns Cleanup function to remove event listeners
 */
export const initializeEditorEventListeners = ({
  containerRef,
  handleDragOver,
  handleDrop,
  setSelectionsOpen,
  setSignaturesOpen
}: {
  containerRef: any;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>, containerRef: any) => void;
  setSelectionsOpen: (isOpen: boolean) => void;
  setSignaturesOpen: (isOpen: boolean) => void;
}) => {
  if (!containerRef?.current?.documentEditor) return () => { };

  // Set up drag and drop
  const editorElement = document.getElementById("container");
  if (editorElement) {
    const handleDragOverNative = (e: DragEvent) =>
      handleDragOver(e as unknown as React.DragEvent<HTMLDivElement>);
    const handleDropNative = (e: DragEvent) =>
      handleDrop(e as unknown as React.DragEvent<HTMLDivElement>, containerRef);

    editorElement.addEventListener("dragover", handleDragOverNative);
    editorElement.addEventListener("drop", handleDropNative);

    // Hide revisions pane
    if (containerRef.current.documentEditor) {
      containerRef.current.documentEditor.showRevisions = false;
    }

    // Setup properties pane button click handler
    const propertiesPaneButton = document.querySelector(
      'button[aria-label="Show properties pane"], button[aria-label="Hide properties pane"]'
    );

    if (propertiesPaneButton) {
      const handlePropertiesPaneClick = () => {
        const isPaneOpen = propertiesPaneButton.getAttribute('aria-pressed') === 'true';

        if (isPaneOpen) {
          setSelectionsOpen(false);
          setSignaturesOpen(false);
        }
      };

      propertiesPaneButton.addEventListener('click', handlePropertiesPaneClick);

      // Cleanup function to remove event listeners
      return () => {
        if (editorElement) {
          editorElement.removeEventListener("dragover", handleDragOverNative);
          editorElement.removeEventListener("drop", handleDropNative);
        }
        propertiesPaneButton.removeEventListener('click', handlePropertiesPaneClick);
      };
    }

    // Cleanup function if propertiesPaneButton not found
    return () => {
      if (editorElement) {
        editorElement.removeEventListener("dragover", handleDragOverNative);
        editorElement.removeEventListener("drop", handleDropNative);
      }
    };
  }

  return () => { };
};

/**
 * Sets up wheel event listener on overlay to enable scrolling
 */
export const setupOverlayScrolling = () => {
  const overlay = document.querySelector(".overlay") as HTMLElement | null;
  const editorContainer = document.querySelector(".editor") as HTMLElement | null;

  if (overlay && editorContainer) {
    overlay.addEventListener("wheel", (e) => {
      editorContainer.scrollBy(0, e.deltaY);
    });
  }
};

/**
 * Fetches all required data for template editing
 * @param params - Object containing required parameters
 * @returns Promise with fetched data
 */
export const fetchTemplateData = async ({
  templateId,
  fieldRulesRowData = []
}: {
  templateId: string | number;
  fieldRulesRowData?: any[];
}) => {
  try {
    const [firmTemplateFields, fieldRules, contentBlocks] = await Promise.all([
      fetchFirmTemplateFieldDetails(),
      fetchTemplateFieldRules(),
      fetchContentBlocksDetails(),
    ]);

    return {
      firmTemplateFields,
      fieldRules,
      contentBlocks
    };
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

/**
 * Prepares recipient options from local storage
 * @returns Object with recipient data
 */
export const prepareRecipientOptions = () => {
  const recipients =
    localStorage.getItem("recipients") !== "null" && localStorage.getItem("recipients")
      ? JSON.parse(localStorage.getItem("recipients") as string)
      : [];

  const defOptions = recipients.map((key: string) => ({
    label: key.replace(/[{{}}]/g, ""),
    value: key.replace(/[{{}}]/g, ""),
  }));

  const tempRecipientOptions = defOptions.length > 0
    ? defOptions
    : [{ label: "ClientEmail", value: "ClientEmail" }];

  // Generate input fields from recipient options
  const inputFields = tempRecipientOptions.map((option: any): any => ({
    id: Date.now() + Math.random(), //NOSONAR
    value: option.label,
  }));

  return {
    recipientOptions: tempRecipientOptions,
    defaultRecipient: defOptions[0] || { label: "ClientEmail", value: "ClientEmail" },
    inputFields
  };
};

/**
 * Updates recipient information in local storage
 * @param selectedRecipient - Currently selected recipient
 * @param recipientOptions - List of available recipients
 */
export const updateRecipientStorage = (
  selectedRecipient: { label: string; value: string },
  recipientOptions: Array<{ label: string; value: string }>
) => {
  localStorage.setItem('SelectedRecipient', selectedRecipient.label);
  localStorage.setItem(
    'CurrentRecipientId',
    (getLabelIndex(selectedRecipient.label, recipientOptions) + 1).toString()
  );
};

/**
 * Filters active rules from template field rules data
 * @param templateFieldRulesRowData - Raw rules data 
 * @returns Array of active rule names
 */
export const filterActiveRules = (templateFieldRulesRowData: any[]) => {
  if (!templateFieldRulesRowData) return [];

  return [
    ...new Set(
      templateFieldRulesRowData
        .filter(row => row.Active !== 'N')
        .map(row => row.RuleName)
    )
  ];
};

export const find_and_replace_all = async (
  container: EditorContainer,
  type: PlaceholderType,
  textToFind: string,
  textToAdd: string
): Promise<boolean | undefined> => {
  if (!container || !container.documentEditor) return;

  const placeholderTextToFind =
    type === "SignatureFields" ? `«${textToFind}:Signer1»` :
      type === "Rules" ? `{${textToFind}}` :
        type === "ContentBlocks" ? `[[${textToFind}]]` :
          type === "SelectedFields" ? `{{${textToFind}}}` :
            textToFind;

  const { searchModule, selection } = container.documentEditor;

  searchModule.findAll(placeholderTextToFind);

  const searchResults = searchModule.searchResults;

  if (searchResults.length > 0) {
    const offsets = searchResults.getTextSearchResultsOffset();

    searchResults.replaceAll(textToAdd);

    offsets.forEach(result => {
      const startOffset = result.startOffset;
      const endOffset = result.endOffset;

      const startOffsetPosition = parseInt(startOffset.split(';').pop() || '0');
      const endPosition = startOffsetPosition + textToAdd.length;

      const arr = endOffset.split(';');
      arr[arr.length - 1] = endPosition.toString();
      const endOffsetPosition = arr.join(';');

      selection.select(startOffset, endOffsetPosition, { preventScroll: true });
      selection.characterFormat.highlightColor = "#ADD8E6";

      setTimeout(() => {
        selection.clear();
      }, 0);
    });
  }

  return true;
};

export const remove_unnecessary_placeholders = async (
  container: EditorContainer,
  type: PlaceholderType,
  textToFind: string
): Promise<boolean | undefined> => {
  if (!container || !container.documentEditor) return;

  const placeholderTextToFind =
    type === "SignatureFields" ? `«${textToFind}:Signer1»` :
      type === "Rules" ? `{${textToFind}}` :
        type === "ContentBlocks" ? `[[${textToFind}]]` :
          type === "SelectedFields" ? `{{${textToFind}}}` :
            textToFind;

  const { searchModule } = container.documentEditor;

  searchModule.findAll(placeholderTextToFind);

  const searchResults = searchModule.searchResults;
  console.log("SEARCH RESULTS FOR REPLACEMENT AT END: ", searchResults, placeholderTextToFind)

  if (searchResults.length > 0) {
    searchResults.replaceAll("");
  }

  return true;
};

export const removeToolbarElements = () => {
  const popup = document.getElementById("container_toolbar_break-popup");
  if (popup) popup.remove();

  const listView = document.getElementById("container_toolbar_break_listView");
  if (listView) listView.remove();
}

export const getFieldRulesRowData = (): any[] => {
  const key = localStorage.getItem("key") || "";
  const savedData = localStorage.getItem("FieldRulesRowData");
  return savedData ? JSON.parse(savedData)[key] ?? [] : [];
}

export const applyTemplateData = (
  firmTemplateFields: any,
  fieldRules: any[],
  contentBlocks: any[],
  setStateFns: {
    setSignaturesList: Function,
    setSelectedFieldsList: Function,
    setTemplateFieldRulesRowData: Function,
    setTemplateFieldRulesList: Function,
    setContentBlocksData: Function,
    setDataLoaded: Function,
    templateId: string | number,
    forwizard?: boolean
  }
) => {
  const uniqueRuleNames = fieldRules.reduce((acc: string[], rule: any) => {
    if (!acc.includes(rule.RuleName)) acc.push(rule.RuleName);
    return acc;
  }, []);

  setStateFns.setSignaturesList(firmTemplateFields.SignatureFields);
  setStateFns.setSelectedFieldsList(firmTemplateFields.SelectedFields);
  setStateFns.setTemplateFieldRulesRowData(fieldRules);
  setStateFns.setTemplateFieldRulesList(uniqueRuleNames);
  setStateFns.setContentBlocksData(contentBlocks);

  if (setStateFns.templateId && !setStateFns.forwizard) {
    setStateFns.setDataLoaded(true);
  }
}

export const convertRecipientsToClientDetails = (
  recipientsJson: any[]
): Record<string, string> => {
  const result: Record<string, string> = {};

  recipientsJson.forEach(({ FieldColumnName, RecipientEmail }) => {
    if (FieldColumnName) {
      result[FieldColumnName] = RecipientEmail;
    }
  });

  return result;
};